import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  onSnapshot,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../utils/firebase";

// Multilingual string type
export interface MultilingualString {
  en: string;
  my: string;
}
//hello
//hello
// Helper function to get localized string
export const getLocalizedString = (text: any, lang: string = "en"): string => {
  if (typeof text === "string") {
    return text; // Backward compatibility: if it's a string, return as is
  }
  return text[lang as keyof MultilingualString] || text.en || "";
};

// Types for Firestore documents
export interface BlogPost {
  id?: string;
  title: any; // Support both multilingual and legacy string format
  content: any;
  excerpt: any;
  author: string;
  image: string;
  tags: string[];
  published: boolean;
  featured: boolean;
  index?: number; // Priority index for sorting (lower numbers appear first)
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  publishedAt?: Timestamp;
}

export interface NewsItem {
  id?: string;
  title: any; // Support both multilingual and legacy string format
  content: any;
  excerpt: any;
  author: string;
  image: string;
  category: string;
  published: boolean;
  featured: boolean;
  index?: number; // Priority index for sorting (lower numbers appear first)
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  publishedAt?: Timestamp;
}

export interface Service {
  id?: string;
  title: any; // Support both multilingual and legacy string format
  description: any;
  shortDescription: any;
  image: string;
  price: number;
  duration: string;
  category: string;
  features: string[];
  published: boolean;
  featured: boolean;
  index?: number; // Priority index for sorting (lower numbers appear first)
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  publishedAt?: Timestamp;
}

export interface Product {
  id?: string;
  title: any; // Support both multilingual and legacy string format
  description: any;
  shortDescription: any;
  image: string;
  price: string;
  category: string;
  features: string[];
  specifications: Record<string, string>;
  inStock: boolean;
  stockQuantity: number;
  published: boolean;
  featured: boolean;
  index?: number; // Priority index for sorting (lower numbers appear first)
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  publishedAt?: Timestamp;
}

// Blog Posts CRUD Operations
export const blogService = {
  // Create a new blog post
  async createBlogPost(
    blogData: Omit<BlogPost, "id" | "createdAt" | "updatedAt">
  ): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, "blogPosts"), {
        ...blogData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        publishedAt: blogData.published ? serverTimestamp() : null,
      });
      return docRef.id;
    } catch (error) {
      console.error("Error creating blog post:", error);
      throw error;
    }
  },

  // Get all blog posts
  async getAllBlogPosts(): Promise<BlogPost[]> {
    try {
      const q = query(collection(db, "blogPosts"));
      const querySnapshot = await getDocs(q);
      const posts = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as BlogPost[];

      // Sort by index (lower numbers first), then by createdAt if index is the same
      return posts.sort((a, b) => {
        const indexA = a.index ?? 999999; // Default to high number if no index
        const indexB = b.index ?? 999999;

        if (indexA !== indexB) {
          return indexA - indexB;
        }

        // If indices are equal, sort by createdAt (newer first)
        const createdAtA = a.createdAt?.toMillis() ?? 0;
        const createdAtB = b.createdAt?.toMillis() ?? 0;
        return createdAtB - createdAtA;
      });
    } catch (error) {
      console.error("Error getting blog posts:", error);
      throw error;
    }
  },

  // Get published blog posts only
  async getPublishedBlogPosts(): Promise<BlogPost[]> {
    try {
      const q = query(
        collection(db, "blogPosts"),
        where("published", "==", true)
      );
      const querySnapshot = await getDocs(q);
      const posts = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as BlogPost[];

      // Sort by index (lower numbers first), then by createdAt if index is the same
      return posts.sort((a, b) => {
        const indexA = a.index ?? 999999; // Default to high number if no index
        const indexB = b.index ?? 999999;

        if (indexA !== indexB) {
          return indexA - indexB;
        }

        // If indices are equal, sort by createdAt (newer first)
        const createdAtA = a.createdAt?.toMillis() ?? 0;
        const createdAtB = b.createdAt?.toMillis() ?? 0;
        return createdAtB - createdAtA;
      });
    } catch (error) {
      console.error("Error getting published blog posts:", error);
      throw error;
    }
  },

  // Get a single blog post by ID
  async getBlogPostById(id: string): Promise<BlogPost | null> {
    try {
      const docRef = doc(db, "blogPosts", id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data(),
        } as BlogPost;
      } else {
        return null;
      }
    } catch (error) {
      console.error("Error getting blog post:", error);
      throw error;
    }
  },

  // Update a blog post
  async updateBlogPost(
    id: string,
    updates: Partial<Omit<BlogPost, "id" | "createdAt">>
  ): Promise<void> {
    try {
      const docRef = doc(db, "blogPosts", id);
      const updateData: any = {
        ...updates,
        updatedAt: serverTimestamp(),
      };

      // Handle publishedAt - only set if published is true, otherwise set to null
      if (updates.published === true) {
        updateData.publishedAt = serverTimestamp();
      } else if (updates.published === false) {
        updateData.publishedAt = null;
      }
      // If published is undefined, don't include publishedAt in the update

      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error("Error updating blog post:", error);
      throw error;
    }
  },

  // Delete a blog post
  async deleteBlogPost(id: string): Promise<void> {
    try {
      const docRef = doc(db, "blogPosts", id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error("Error deleting blog post:", error);
      throw error;
    }
  },

  // Subscribe to real-time updates for blog posts
  subscribeToBlogPosts(callback: (posts: BlogPost[]) => void) {
    const q = query(collection(db, "blogPosts"));
    return onSnapshot(q, (querySnapshot) => {
      const posts = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as BlogPost[];

      // Sort by index (lower numbers first), then by createdAt if index is the same
      const sortedPosts = posts.sort((a, b) => {
        const indexA = a.index ?? 999999;
        const indexB = b.index ?? 999999;

        if (indexA !== indexB) {
          return indexA - indexB;
        }

        const createdAtA = a.createdAt?.toMillis() ?? 0;
        const createdAtB = b.createdAt?.toMillis() ?? 0;
        return createdAtB - createdAtA;
      });

      callback(sortedPosts);
    });
  },

  // Subscribe to published blog posts only
  subscribeToPublishedBlogPosts(callback: (posts: BlogPost[]) => void) {
    const q = query(
      collection(db, "blogPosts"),
      where("published", "==", true)
    );
    return onSnapshot(q, (querySnapshot) => {
      const posts = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as BlogPost[];

      // Sort by index (lower numbers first), then by createdAt if index is the same
      const sortedPosts = posts.sort((a, b) => {
        const indexA = a.index ?? 999999;
        const indexB = b.index ?? 999999;

        if (indexA !== indexB) {
          return indexA - indexB;
        }

        const createdAtA = a.createdAt?.toMillis() ?? 0;
        const createdAtB = b.createdAt?.toMillis() ?? 0;
        return createdAtB - createdAtA;
      });

      callback(sortedPosts);
    });
  },
};

// News Items CRUD Operations
export const newsService = {
  // Create a new news item
  async createNewsItem(
    newsData: Omit<NewsItem, "id" | "createdAt" | "updatedAt">,
    customCreatedAt?: Timestamp
  ): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, "newsItems"), {
        ...newsData,
        createdAt: customCreatedAt || serverTimestamp(),
        updatedAt: serverTimestamp(),
        publishedAt: newsData.published ? (customCreatedAt || serverTimestamp()) : null,
      });
      return docRef.id;
    } catch (error) {
      console.error("Error creating news item:", error);
      throw error;
    }
  },

  // Get all news items
  async getAllNewsItems(): Promise<NewsItem[]> {
    try {
      const q = query(collection(db, "newsItems"));
      const querySnapshot = await getDocs(q);
      const items = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as NewsItem[];

      // Sort by index (lower numbers first), then by createdAt if index is the same
      return items.sort((a, b) => {
        const indexA = a.index ?? 999999; // Default to high number if no index
        const indexB = b.index ?? 999999;

        if (indexA !== indexB) {
          return indexA - indexB;
        }

        // If indices are equal, sort by createdAt (newer first)
        const createdAtA = a.createdAt?.toMillis() ?? 0;
        const createdAtB = b.createdAt?.toMillis() ?? 0;
        return createdAtB - createdAtA;
      });
    } catch (error) {
      console.error("Error getting news items:", error);
      throw error;
    }
  },

  // Get published news items only
  async getPublishedNewsItems(): Promise<NewsItem[]> {
    try {
      const q = query(
        collection(db, "newsItems"),
        where("published", "==", true)
      );
      const querySnapshot = await getDocs(q);
      const items = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as NewsItem[];

      // Sort by index (lower numbers first), then by createdAt if index is the same
      return items.sort((a, b) => {
        const indexA = a.index ?? 999999; // Default to high number if no index
        const indexB = b.index ?? 999999;

        if (indexA !== indexB) {
          return indexA - indexB;
        }

        // If indices are equal, sort by createdAt (newer first)
        const createdAtA = a.createdAt?.toMillis() ?? 0;
        const createdAtB = b.createdAt?.toMillis() ?? 0;
        return createdAtB - createdAtA;
      });
    } catch (error) {
      console.error("Error getting published news items:", error);
      throw error;
    }
  },

  // Get a single news item by ID
  async getNewsItemById(id: string): Promise<NewsItem | null> {
    try {
      const docRef = doc(db, "newsItems", id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data(),
        } as NewsItem;
      } else {
        return null;
      }
    } catch (error) {
      console.error("Error getting news item:", error);
      throw error;
    }
  },

  // Update a news item
  async updateNewsItem(
    id: string,
    updates: Partial<Omit<NewsItem, "id" | "createdAt">>,
    customCreatedAt?: Timestamp
  ): Promise<void> {
    try {
      const docRef = doc(db, "newsItems", id);
      const updateData: any = {
        ...updates,
        updatedAt: serverTimestamp(),
      };

      // Update createdAt if custom date is provided
      if (customCreatedAt) {
        updateData.createdAt = customCreatedAt;
      }

      // Handle publishedAt - only set if published is true, otherwise set to null
      if (updates.published === true) {
        updateData.publishedAt = customCreatedAt || serverTimestamp();
      } else if (updates.published === false) {
        updateData.publishedAt = null;
      }
      // If published is undefined, don't include publishedAt in the update

      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error("Error updating news item:", error);
      throw error;
    }
  },

  // Delete a news item
  async deleteNewsItem(id: string): Promise<void> {
    try {
      const docRef = doc(db, "newsItems", id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error("Error deleting news item:", error);
      throw error;
    }
  },

  // Subscribe to real-time updates for news items
  subscribeToNewsItems(callback: (items: NewsItem[]) => void) {
    const q = query(collection(db, "newsItems"));
    return onSnapshot(q, (querySnapshot) => {
      const items = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as NewsItem[];

      // Sort by index (lower numbers first), then by createdAt if index is the same
      const sortedItems = items.sort((a, b) => {
        const indexA = a.index ?? 999999;
        const indexB = b.index ?? 999999;

        if (indexA !== indexB) {
          return indexA - indexB;
        }

        const createdAtA = a.createdAt?.toMillis() ?? 0;
        const createdAtB = b.createdAt?.toMillis() ?? 0;
        return createdAtB - createdAtA;
      });

      callback(sortedItems);
    });
  },

  // Subscribe to published news items only
  subscribeToPublishedNewsItems(callback: (items: NewsItem[]) => void) {
    const q = query(
      collection(db, "newsItems"),
      where("published", "==", true)
    );
    return onSnapshot(q, (querySnapshot) => {
      const items = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as NewsItem[];

      // Sort by index (lower numbers first), then by createdAt if index is the same
      const sortedItems = items.sort((a, b) => {
        const indexA = a.index ?? 999999;
        const indexB = b.index ?? 999999;

        if (indexA !== indexB) {
          return indexA - indexB;
        }

        const createdAtA = a.createdAt?.toMillis() ?? 0;
        const createdAtB = b.createdAt?.toMillis() ?? 0;
        return createdAtB - createdAtA;
      });

      callback(sortedItems);
    });
  },
};

// Services CRUD Operations
export const serviceService = {
  // Create a new service
  async createService(
    serviceData: Omit<Service, "id" | "createdAt" | "updatedAt">
  ): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, "services"), {
        ...serviceData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        publishedAt: serviceData.published ? serverTimestamp() : null,
      });
      return docRef.id;
    } catch (error) {
      console.error("Error creating service:", error);
      throw error;
    }
  },

  // Get all services
  async getAllServices(): Promise<Service[]> {
    try {
      const querySnapshot = await getDocs(collection(db, "services"));
      const services = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Service[];

      // Sort by index (lower numbers first), then by createdAt if index is the same
      return services.sort((a, b) => {
        const indexA = a.index ?? 999999; // Default to high number if no index
        const indexB = b.index ?? 999999;

        if (indexA !== indexB) {
          return indexA - indexB;
        }

        // If indices are equal, sort by createdAt (newer first)
        const createdAtA = a.createdAt?.toMillis() ?? 0;
        const createdAtB = b.createdAt?.toMillis() ?? 0;
        return createdAtB - createdAtA;
      });
    } catch (error) {
      console.error("Error getting services:", error);
      throw error;
    }
  },

  // Get a single service
  async getService(id: string): Promise<Service | null> {
    try {
      const docRef = doc(db, "services", id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as Service;
      }
      return null;
    } catch (error) {
      console.error("Error getting service:", error);
      throw error;
    }
  },

  // Update a service
  async updateService(
    id: string,
    updates: Partial<Omit<Service, "id" | "createdAt">>
  ): Promise<void> {
    try {
      const docRef = doc(db, "services", id);
      const updateData: any = {
        ...updates,
        updatedAt: serverTimestamp(),
      };

      // Handle publishedAt - only set if published is true, otherwise set to null
      if (updates.published === true) {
        updateData.publishedAt = serverTimestamp();
      } else if (updates.published === false) {
        updateData.publishedAt = null;
      }
      // If published is undefined, don't include publishedAt in the update

      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error("Error updating service:", error);
      throw error;
    }
  },

  // Delete a service
  async deleteService(id: string): Promise<void> {
    try {
      const docRef = doc(db, "services", id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error("Error deleting service:", error);
      throw error;
    }
  },

  // Subscribe to published services in real-time
  subscribeToPublishedServices(callback: (services: Service[]) => void) {
    const q = query(collection(db, "services"), where("published", "==", true));

    return onSnapshot(q, (snapshot) => {
      const services = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Service[];

      // Sort by index (lower numbers first), then by createdAt if index is the same
      const sortedServices = services.sort((a, b) => {
        const indexA = a.index ?? 999999;
        const indexB = b.index ?? 999999;

        if (indexA !== indexB) {
          return indexA - indexB;
        }

        const createdAtA = a.createdAt?.toMillis() ?? 0;
        const createdAtB = b.createdAt?.toMillis() ?? 0;
        return createdAtB - createdAtA;
      });

      callback(sortedServices);
    });
  },
};

// Products CRUD Operations
export const productService = {
  // Create a new product
  async createProduct(
    productData: Omit<Product, "id" | "createdAt" | "updatedAt">
  ): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, "products"), {
        ...productData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        publishedAt: productData.published ? serverTimestamp() : null,
      });
      return docRef.id;
    } catch (error) {
      console.error("Error creating product:", error);
      throw error;
    }
  },

  // Get all products
  async getAllProducts(): Promise<Product[]> {
    try {
      const querySnapshot = await getDocs(collection(db, "products"));
      const products = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Product[];

      // Sort by index (lower numbers first), then by createdAt if index is the same
      return products.sort((a, b) => {
        const indexA = a.index ?? 999999; // Default to high number if no index
        const indexB = b.index ?? 999999;

        if (indexA !== indexB) {
          return indexA - indexB;
        }

        // If indices are equal, sort by createdAt (newer first)
        const createdAtA = a.createdAt?.toMillis() ?? 0;
        const createdAtB = b.createdAt?.toMillis() ?? 0;
        return createdAtB - createdAtA;
      });
    } catch (error) {
      console.error("Error getting products:", error);
      throw error;
    }
  },

  // Get a single product
  async getProduct(id: string): Promise<Product | null> {
    try {
      const docRef = doc(db, "products", id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as Product;
      }
      return null;
    } catch (error) {
      console.error("Error getting product:", error);
      throw error;
    }
  },

  // Update a product
  async updateProduct(
    id: string,
    updates: Partial<Omit<Product, "id" | "createdAt">>
  ): Promise<void> {
    try {
      const docRef = doc(db, "products", id);
      const updateData: any = {
        ...updates,
        updatedAt: serverTimestamp(),
      };

      // Handle publishedAt - only set if published is true, otherwise omit the field
      if (updates.published === true) {
        updateData.publishedAt = serverTimestamp();
      } else if (updates.published === false) {
        updateData.publishedAt = null;
      }
      // If published is undefined, don't include publishedAt in the update

      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error("Error updating product:", error);
      throw error;
    }
  },

  // Delete a product
  async deleteProduct(id: string): Promise<void> {
    try {
      const docRef = doc(db, "products", id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error("Error deleting product:", error);
      throw error;
    }
  },

  // Subscribe to published products in real-time
  subscribeToPublishedProducts(callback: (products: Product[]) => void) {
    const q = query(collection(db, "products"), where("published", "==", true));

    return onSnapshot(q, (snapshot) => {
      const products = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Product[];

      // Sort by index (lower numbers first), then by createdAt if index is the same
      const sortedProducts = products.sort((a, b) => {
        const indexA = a.index ?? 999999; // Default to high number if no index
        const indexB = b.index ?? 999999;

        if (indexA !== indexB) {
          return indexA - indexB;
        }

        // If indices are equal, sort by createdAt (newer first)
        const createdAtA = a.createdAt?.toMillis() ?? 0;
        const createdAtB = b.createdAt?.toMillis() ?? 0;
        return createdAtB - createdAtA;
      });

      callback(sortedProducts);
    });
  },
};

// Hero Slide Interface
export interface HeroSlide {
  id?: string;
  title: any; // Support both multilingual and legacy string format
  subtitle: any;
  description?: any;
  image: string;
  cta: {
    products: any;
    contact: any;
  };
  active: boolean; // Whether this slide should be displayed
  index?: number; // Priority index for sorting (lower numbers appear first)
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

// Hero Slides CRUD Operations
export const heroService = {
  // Create a new hero slide
  async createHeroSlide(
    heroData: Omit<HeroSlide, "id" | "createdAt" | "updatedAt">
  ): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, "heroSlides"), {
        ...heroData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return docRef.id;
    } catch (error) {
      console.error("Error creating hero slide:", error);
      throw error;
    }
  },

  // Get all hero slides
  async getAllHeroSlides(): Promise<HeroSlide[]> {
    try {
      const querySnapshot = await getDocs(collection(db, "heroSlides"));
      const slides = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as HeroSlide[];

      // Sort by index (lower numbers first), then by createdAt if index is the same
      return slides.sort((a, b) => {
        const indexA = a.index ?? 999999;
        const indexB = b.index ?? 999999;

        if (indexA !== indexB) {
          return indexA - indexB;
        }

        const createdAtA = a.createdAt?.toMillis() ?? 0;
        const createdAtB = b.createdAt?.toMillis() ?? 0;
        return createdAtB - createdAtA;
      });
    } catch (error) {
      console.error("Error getting hero slides:", error);
      throw error;
    }
  },

  // Get active hero slides only
  async getActiveHeroSlides(): Promise<HeroSlide[]> {
    try {
      const q = query(
        collection(db, "heroSlides"),
        where("active", "==", true)
      );
      const querySnapshot = await getDocs(q);
      const slides = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as HeroSlide[];

      // Sort by index (lower numbers first), then by createdAt if index is the same
      return slides.sort((a, b) => {
        const indexA = a.index ?? 999999;
        const indexB = b.index ?? 999999;

        if (indexA !== indexB) {
          return indexA - indexB;
        }

        const createdAtA = a.createdAt?.toMillis() ?? 0;
        const createdAtB = b.createdAt?.toMillis() ?? 0;
        return createdAtB - createdAtA;
      });
    } catch (error) {
      console.error("Error getting active hero slides:", error);
      throw error;
    }
  },

  // Get a single hero slide by ID
  async getHeroSlideById(id: string): Promise<HeroSlide | null> {
    try {
      const docRef = doc(db, "heroSlides", id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data(),
        } as HeroSlide;
      } else {
        return null;
      }
    } catch (error) {
      console.error("Error getting hero slide:", error);
      throw error;
    }
  },

  // Update a hero slide
  async updateHeroSlide(
    id: string,
    updates: Partial<Omit<HeroSlide, "id" | "createdAt">>
  ): Promise<void> {
    try {
      const docRef = doc(db, "heroSlides", id);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error updating hero slide:", error);
      throw error;
    }
  },

  // Delete a hero slide
  async deleteHeroSlide(id: string): Promise<void> {
    try {
      const docRef = doc(db, "heroSlides", id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error("Error deleting hero slide:", error);
      throw error;
    }
  },

  // Subscribe to active hero slides in real-time
  subscribeToActiveHeroSlides(callback: (slides: HeroSlide[]) => void) {
    const q = query(collection(db, "heroSlides"), where("active", "==", true));

    return onSnapshot(q, (snapshot) => {
      const slides = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as HeroSlide[];

      // Sort by index (lower numbers first), then by createdAt if index is the same
      const sortedSlides = slides.sort((a, b) => {
        const indexA = a.index ?? 999999;
        const indexB = b.index ?? 999999;

        if (indexA !== indexB) {
          return indexA - indexB;
        }

        const createdAtA = a.createdAt?.toMillis() ?? 0;
        const createdAtB = b.createdAt?.toMillis() ?? 0;
        return createdAtB - createdAtA;
      });

      callback(sortedSlides);
    });
  },
};

// Image Upload Utility - Google Image Upload API
export const imageUploadService = {
  // Upload image to Google Image Upload API - works directly from browser, no backend needed
  // Note: folder parameter is kept for backward compatibility but not used
  async uploadImage(file: File, _folder: string = "services"): Promise<string> {
    try {
      // Create FormData with the file
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(
        "https://google-image-upload.vercel.app/api/upload",
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { message: errorText || "Upload failed" };
        }
        console.error("Image Upload API Error:", errorData);
        throw new Error(
          errorData.error?.message ||
            errorData.message ||
            `Upload failed with status: ${response.status}`
        );
      }

      const data = await response.json();

      // Check for direct fileUrl first (primary response format)
      if (data.fileUrl) {
        return data.fileUrl;
      }

      // Fallback to other possible URL fields
      if (data.imageUrl || data.url || data.directUrl) {
        return data.imageUrl || data.url || data.directUrl;
      }

      // Legacy Google Drive format support (for backward compatibility)
      if (data.fileId) {
        return `https://drive.google.com/thumbnail?id=${data.fileId}&sz=w1000`;
      }

      if (data.downloadLink) {
        const match = data.downloadLink.match(/[?&]id=([^&]+)/);
        if (match && match[1]) {
          return `https://drive.google.com/thumbnail?id=${match[1]}&sz=w1000`;
        }
        return data.downloadLink.replace(/&export=download/g, "");
      }

      if (data.viewLink) {
        const match = data.viewLink.match(/\/d\/([a-zA-Z0-9_-]+)/);
        if (match && match[1]) {
          return `https://drive.google.com/thumbnail?id=${match[1]}&sz=w1000`;
        }
      }

      // Additional fallback checks
      if (data.data?.url) {
        return data.data.url;
      }

      if (data.data?.display_url) {
        return data.data.display_url;
      }

      if (data.image?.url) {
        return data.image.url;
      }

      // If no valid URL found, throw error
      console.error("Image Upload API Response:", data);
      throw new Error(
        "Upload failed - invalid response format from upload API"
      );
    } catch (error) {
      console.error("Error uploading image:", error);
      throw error;
    }
  },
};
